import {useSignal, useSignals} from '@preact/signals-react/runtime'
import {useQueryClient} from '@tanstack/react-query'
import {Button} from 'primereact/button'
import {Dialog} from 'primereact/dialog'
import {Divider} from 'primereact/divider'
import React, {
    forwardRef,
    useCallback,
    useImperativeHandle,
    useRef,
} from 'react'
import {SystemRole} from '../../../Constants'
import {useAppConfig} from '../../../context/app/AppContext'
import {useUserHospital} from '../../../context/hospital/UserHospitalContext'
import {
    QueryCRUDTypes,
    QueryKeyManager,
} from '../../../query-manager/QueryKeysManager'
import {QueryKeyTypes} from '../../../query-manager/QueryKeyTypes'
import {checkEmptyString, validEmail} from '../../../utils/CommonUtils'
import AddressForm from '../../address/AddressForm'
import {useAuth} from '../../authentication/AuthProvider'
import {component} from '../../form/Constant'
import ImageUploader from '../../image/ImageUploader'
import PageContentWrapper from '../../layout/PageContentWrapper'
import StyledRequireText from '../../layout/StyledRequireText'
import {severityNotification} from '../../notification/NotificationConstant'
import {showNotification} from '../../notification/NotificationService'
import {usePatientCRUD} from '../../patient/query-keys/PatientKeysManager'
import StyledDropdown from '../components/StyledDropdown'
import StyledInput from '../components/StyledInput'
import StyledInputMask from '../components/StyledInputMask'
import StyledHCPractitionerOptions from '../styled-options/StyledHCPractitionerOptions'
import StyledHospitalOptions from '../styled-options/StyledHospitalOptions'
import StyledPathwayOptions from '../styled-options/StyledPathwayOptions'
import {keys, patientList, toPostDataKeys} from './Constants'
const StyledPatientForm = forwardRef((props, ref) => {
    useSignals()
    useImperativeHandle(ref, () => ({
        showForm: handleShowForm,
        hideForm: handleHideForm,
    }))

    //#region Context
    const {user} = useAuth()
    const queryClient = useQueryClient()

    const {userRole} = useAppConfig()
    const {currentHospital} = useUserHospital()
    //#endregion

    //#region State
    const visible = useSignal(false)
    const formData = useSignal({})
    const formRefs = useRef([])

    const handleShowForm = useCallback(() => {
        visible.value = true
    }, [])

    const handleHideForm = useCallback(() => {
        visible.value = false
    }, [])
    //#endregion

    //#region CRUD Function
    const {mutateAsync: createOrUpdatePatient} = usePatientCRUD(
        QueryCRUDTypes.create,
        user?.id,
    )

    const handleSubmit = async () => {
        let toPostData = {
            ...formData.value,
        }

        toPostDataKeys?.map(key => {
            if (key === keys.address) {
                toPostData = {
                    ...toPostData,
                    ...formRefs.current[key]?.getValue(),
                }
            } else {
                toPostData[key] = formRefs.current[key]?.getValue()
            }
        })

        if (!toPostData.hospitalId) {
            showNotification(
                severityNotification.error,
                'Hospital is required !!',
            )
            return
        }

        if (!toPostData?.email || toPostData?.email?.length < 0) {
            showNotification(severityNotification.error, 'Email is required !!')
            return
        }

        if (!validEmail(toPostData?.email)) {
            showNotification(severityNotification.error, 'Email is invalid !!')
            return
        }

        if (
            !toPostData?.firstName ||
            toPostData?.firstName?.length < 0 ||
            !!checkEmptyString(toPostData?.firstName)
        ) {
            showNotification(
                severityNotification.error,
                'First Name is required !!',
            )
            return
        }

        const userResponse = await createOrUpdatePatient(toPostData)

        if (userResponse && userResponse?.id) {
            const {patientKeys} = QueryKeyManager(
                QueryKeyTypes.patient,
                user?.id,
            )

            queryClient.invalidateQueries({
                queryKey: patientKeys.pattern(),
            })

            handleHideForm()
        }
    }
    //#endregion

    return (
        <Dialog
            id="blockscroll-sidebar"
            className="p-sidebar-full"
            visible={visible.value}
            onHide={() => handleHideForm()}
            blockScroll
            header="PATIENT FORM"
            fullScreen>
            <PageContentWrapper>
                <React.Fragment>
                    <div className="grid">
                        {/* PRE-CONDITION */}
                        <div className="col-4 relative">
                            <div className="sticky">
                                <h5 className="font-bold underline-60">
                                    Hospital Information
                                </h5>
                                <p>Select hospitals from the list below</p>
                            </div>
                        </div>
                        <div className="col-8">
                            <div className="">
                                {/* Component Header */}
                                <div className="flex align-items-center justify-content-between">
                                    <div>
                                        <h6 className="text-700 font-semibold mb-1">
                                            Hospital Options
                                        </h6>
                                    </div>

                                    <div>
                                        <StyledRequireText />
                                    </div>
                                </div>
                                {/* End Region */}
                                <StyledHospitalOptions
                                    ref={el =>
                                        (formRefs.current[keys.hospitalId] = el)
                                    }
                                    optionType={component.dropdownGroup}
                                    groupByIsNull={null}
                                    placeholder={'Please select hospital'}
                                    value={
                                        userRole === SystemRole.USER_WITH_ROLE
                                            ? currentHospital?.id
                                            : null
                                    }
                                    onChange={value => {
                                        formRefs.current[
                                            keys.hcpIds
                                        ]?.changeHospitalId(value)
                                    }}
                                />
                            </div>
                        </div>

                        <Divider
                            className="my-1 custom-divider"
                            type="dashed"
                        />

                        <div className="col-4 relative">
                            <div className="sticky">
                                <h4 className="font-bold underline-60">
                                    Assignment
                                </h4>
                                <p>Assign the patient to those listed groups</p>
                            </div>
                        </div>
                        <div className="col-8">
                            <div className="">
                                {/* Components */}
                                <div className="flex align-items-center justify-content-between">
                                    <div>
                                        <h6 className="text-700 font-semibold mb-1">
                                            Health Care Practitioner Options
                                        </h6>
                                    </div>
                                </div>
                                {/* End Region */}
                                <StyledHCPractitionerOptions
                                    ref={el =>
                                        (formRefs.current[keys.hcpIds] = el)
                                    }
                                    optionType={component.multiSelect}
                                    groupByIsNull={null}
                                />

                                <p className=" font-bold text-sm text-500">
                                    <i className="pi pi-info-circle mr-2"></i>
                                    <span className="">
                                        The list of Health Care Practitioners is
                                        based on the selected hospital and only
                                        {` `}
                                        <span className="text-teal-500 font-bold">
                                            qualified Health Care Practitioners
                                        </span>{' '}
                                        will be displayed.
                                    </span>
                                </p>

                                <div className="flex align-items-center justify-content-between">
                                    <div>
                                        <h6 className="text-700 font-semibold mb-1">
                                            Pathway Options
                                        </h6>
                                    </div>
                                </div>
                                {/* End Region */}
                                <StyledPathwayOptions
                                    ref={el =>
                                        (formRefs.current[keys.pathwayIds] = el)
                                    }
                                    optionType={component.multiSelect}
                                    groupByIsNull={null}
                                />
                            </div>
                        </div>

                        <Divider type="dashed" />

                        {/* END REGION */}

                        {patientList?.map((item, index) => (
                            <React.Fragment key={item?.key}>
                                {/* Group Header */}
                                <div className="col-4 relative">
                                    <div className="sticky">
                                        <h4 className="font-bold mb-1 underline-60">
                                            {item.label}
                                        </h4>
                                        <p className="">{item?.description}</p>
                                    </div>
                                </div>
                                {/* End region */}

                                {/* Components */}
                                {item?.children?.length > 0 && (
                                    <div className="col-8">
                                        {item?.children?.map(child => (
                                            <div
                                                key={child?.key}
                                                className="mb-3">
                                                {/* Component Header */}
                                                <div className="flex align-items-center justify-content-between">
                                                    <div>
                                                        <h6 className="text-700 font-semibold mb-1">
                                                            {child?.label}
                                                        </h6>
                                                    </div>

                                                    <div>
                                                        {child?.required && (
                                                            <StyledRequireText />
                                                        )}
                                                    </div>
                                                </div>
                                                {/* End Region */}

                                                {/* Render Child Components */}
                                                {child?.component ===
                                                    component.inputText && (
                                                    <StyledInput
                                                        ref={el =>
                                                            (formRefs.current[
                                                                child.key
                                                            ] = el)
                                                        }
                                                        value={
                                                            formData.value[
                                                                child?.key
                                                            ] || ''
                                                        }
                                                        placeholder={
                                                            child?.placeholder
                                                        }
                                                        keyfilter={
                                                            child?.keyFilter
                                                                ? child.keyFilter
                                                                : null
                                                        }
                                                        transformType={
                                                            child?.transformType
                                                        }
                                                    />
                                                )}

                                                {child?.component ===
                                                    component.inputMask && (
                                                    <StyledInputMask
                                                        ref={el =>
                                                            (formRefs.current[
                                                                child.key
                                                            ] = el)
                                                        }
                                                        value={
                                                            formData.value[
                                                                child?.key
                                                            ] || ''
                                                        }
                                                        placeholder={
                                                            child?.placeholder
                                                        }
                                                    />
                                                )}

                                                {child?.component ===
                                                    component.fileUpload && (
                                                    <ImageUploader
                                                        id={child.key}
                                                        ref={el =>
                                                            (formRefs.current[
                                                                child.key
                                                            ] = el)
                                                        }
                                                        initialImage={
                                                            formData.value[
                                                                child?.key
                                                            ]
                                                        }
                                                        multiple={
                                                            !!item?.multiple
                                                        }
                                                        extraData={
                                                            !!item?.extraData
                                                        }
                                                    />
                                                )}

                                                {child?.component ===
                                                    component.dropdown && (
                                                    <React.Fragment>
                                                        <StyledDropdown
                                                            ref={el =>
                                                                (formRefs.current[
                                                                    child.key
                                                                ] = el)
                                                            }
                                                            value={
                                                                formData.value[
                                                                    child?.key
                                                                ] ||
                                                                child?.defaultValue
                                                            }
                                                            options={
                                                                child?.options ||
                                                                []
                                                            }
                                                        />
                                                    </React.Fragment>
                                                )}

                                                {child?.component ===
                                                    component.address && (
                                                    <AddressForm
                                                        id={child.key}
                                                        ref={el =>
                                                            (formRefs.current[
                                                                child.key
                                                            ] = el)
                                                        }
                                                        className={'grid'}
                                                        childrenClassName={
                                                            'col-6'
                                                        }
                                                        defaultValue={
                                                            formData.value
                                                                ? {
                                                                      ...formData.value,
                                                                  }
                                                                : {}
                                                        }
                                                    />
                                                )}
                                                {/* End Region */}

                                                {/* DESCRIPTION */}
                                                {child?.description &&
                                                    (typeof child?.description ===
                                                    'string' ? (
                                                        <p>
                                                            {child?.description}
                                                        </p>
                                                    ) : (
                                                        child?.description
                                                    ))}
                                            </div>
                                        ))}
                                    </div>
                                )}
                                <Divider type="dashed" />
                            </React.Fragment>
                        ))}
                    </div>
                </React.Fragment>

                {/* Action Button */}
                <Button
                    className="col-12 h-3rem"
                    label="SUBMIT"
                    icon="pi pi-send"
                    onClick={() => handleSubmit()}
                />
            </PageContentWrapper>
        </Dialog>
    )
})

export default StyledPatientForm
